import {
    Button,
    Card,
    CardBody,
    CardFooter,
    Flex,
    HStack,
    Icon,
    IconButton,
    Image,
    Input,
    Stack,
    Text,
    VisuallyHidden
} from "@chakra-ui/react";
import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {FaTrash} from "react-icons/fa";
import {boatService} from "../../../../services/boatService";
import {BOAT_MAX_IMAGES, ENABLE_BOAT_VIDEO_UPLOAD} from "../../../../constants/config";

function BoatImagesForm({onSubmit, onBack}) {

    const {t} = useTranslation();

    const fileImageInputRef = useRef(null);
    const fileVideoInputRef = useRef(null);

    const [newImage, setNewImage] = useState(null);
    const [previewImages, setPreviewImages] = useState([]);
    const [uploadedImages, setUploadedImages] = useState([]);
    const [newVideo, setNewVideo] = useState(null);
    const [uploadedVideos, setUploadedVideos] = useState([]);
    const [previewVideos, setPreviewVideos] = useState([]);

    useEffect(() => {
        if (newImage) {
            const newImages = [...previewImages];
            newImages.push(newImage);
            setPreviewImages(newImages);
            setNewImage(null)
        }
    }, [newImage])

    useEffect(() => {
        if (newVideo) {
            const newVideos = [...previewVideos];
            newVideos.push(newVideo);
            setPreviewVideos(newVideos);
            setNewVideo(null)
        }
    }, [newVideo])

    const handleOnSubmit = () => {
        onSubmit({
            imagens: uploadedImages,
            videos: uploadedVideos,
        })
    }

    const onClickBack = () => {

    }

    const onSelectFiles = async (newFiles) => {
        const remaining = BOAT_MAX_IMAGES - uploadedImages.length;
        if (remaining <= 0) {
            return;
        }
        const filesToUpload = Array.from(newFiles).slice(0, remaining);
        let index = uploadedImages.length;
        for (const file of filesToUpload) {
            await handleSelectImage(file, index++);
            await new Promise(resolve => setTimeout(resolve, 1000));
        }
    }

    const handleSelectImage = async (file, index) => {
        const fileReader = new FileReader();
        fileReader.addEventListener("load", () => {
            const extension = file.name?.split('.').pop();
            handleUploadImage(fileReader.result, index, extension)
        });
        fileReader.readAsArrayBuffer(file);
    }

    const handleUploadImage = async (fileResult, index, extension) => {
        const fileName = `${new Date().toISOString().replaceAll(":", "-")}-${index}.${extension}`;

        const formData = new FormData();
        formData.set('file', new Blob([fileResult]), fileName);
        formData.set('fileName', fileName);
        formData.set('key', `${fileName}`)

        try {
            const response = await boatService.uploadAsset(formData);

            const newUploadImages = uploadedImages;
            newUploadImages.push(response.fileName)
            setUploadedImages(newUploadImages);

            setNewImage(response.url);
        } catch (error) {
            console.error(error);
        }
    }

    const onDeleteImage = (index) => {
        const newImages = previewImages;
        delete newImages[index];
        setPreviewImages(newImages.filter(img => img !== undefined));

        const newUploadedImages = uploadedImages;
        delete newUploadedImages[index];
        setUploadedImages(newUploadedImages.filter(img => img !== undefined));
    }

    const onSelectVideos = async (newFiles) => {
        let index = uploadedVideos.length;
        for (const file of newFiles) {
            await handleSelectVideo(file, index++);
            await new Promise(resolve => setTimeout(resolve, 1000));
        }
    }

    const handleSelectVideo = async (file, index) => {
        const fileReader = new FileReader();
        fileReader.addEventListener("load", () => {
            const extension = file.name?.split('.').pop();
            handleUploadVideo(fileReader.result, index, extension)
        });
        fileReader.readAsArrayBuffer(file);
    }

    const handleUploadVideo = async (fileResult, index, extension) => {
        const fileName = `${new Date().toISOString().replaceAll(":", "-")}-${index}.${extension}`;

        const formData = new FormData();
        formData.set('file', new Blob([fileResult]), fileName);
        formData.set('fileName', fileName);
        formData.set('key', `boat/assets/${fileName}`)

        try {
            const response = await boatService.uploadAsset(formData);

            const newUploadVideos = [...uploadedVideos];
            newUploadVideos.push(response.fileName)
            setUploadedVideos(newUploadVideos);

            setNewVideo(response.url);
        } catch (error) {
            console.error(error);
        }
    }

    const onDeleteVideo = (index) => {
        const newVideos = previewVideos;
        delete newVideos[index];
        setPreviewImages(newVideos.filter(video => video !== undefined));

        const newUploadedVideos = uploadedVideos;
        delete newUploadedVideos[index];
        setUploadedImages(newUploadedVideos.filter(video => video !== undefined));
    }

    return (
        <>
            <Stack>
                <Text fontWeight="bold">{t('boat.new.images')}</Text>

                <Stack>
                    <Button
                        w="150px"
                        variant="dark"
                        fontSize="sm"
                        isDisabled={previewImages.length >= BOAT_MAX_IMAGES}
                        onClick={() => fileImageInputRef.current.click()}
                    >
                        {t('boat.actions.add-image')}
                    </Button>

                    <VisuallyHidden>
                        <Input
                            ref={fileImageInputRef}
                            type="file"
                            fontSize="sm"
                            variant="main"
                            className="file-upload"
                            accept="image/png, image/jpeg"
                            onChange={(event) => onSelectFiles(event.target.files)}
                            multiple
                        />
                    </VisuallyHidden>

                    <HStack spacing={2}>
                        {previewImages.map((img, index) => (
                            <Card maxW='sm' key={`image-${index}`}>
                                <CardBody>
                                    <Image src={img} width="200px"/>
                                </CardBody>
                                <CardFooter>
                                    <IconButton
                                        size="xs"
                                        colorScheme="red"
                                        aria-label={t("system.actions.clear")}
                                        onClick={(e) => onDeleteImage(index)}
                                        icon={<Icon as={FaTrash}/>}
                                    />
                                </CardFooter>
                            </Card>
                        ))}
                    </HStack>
                </Stack>

                {ENABLE_BOAT_VIDEO_UPLOAD && (
                    <>
                        <Text fontWeight="bold">{t('boat.new.videos')}</Text>

                        <Stack>
                            <Button
                                w="150px"
                                variant="dark"
                                fontSize="sm"
                                onClick={() => fileVideoInputRef.current.click()}
                            >
                                {t('boat.actions.add-video')}
                            </Button>

                            <VisuallyHidden>
                                <Input
                                    ref={fileVideoInputRef}
                                    type="file"
                                    fontSize="sm"
                                    variant="main"
                                    className="file-upload"
                                    accept="video/mov, video/mp4"
                                    onChange={(event) => onSelectVideos(event.target.files)}
                                    multiple
                                />
                            </VisuallyHidden>

                            <HStack spacing={2}>
                                {previewVideos.map((videoUrl, index) => (
                                    <Card maxW='sm' key={`video-card-${index}`}>
                                        <CardBody>
                                            <video
                                                width={150}
                                                height={150}
                                                controls
                                                src={videoUrl}
                                            />
                                        </CardBody>
                                        <CardFooter>
                                            <IconButton
                                                size="xs"
                                                colorScheme="red"
                                                aria-label={t("system.actions.clear")}
                                                onClick={(e) => onDeleteVideo(index)}
                                                icon={<Icon as={FaTrash}/>}
                                            />
                                        </CardFooter>
                                    </Card>
                                ))}
                            </HStack>
                        </Stack>
                    </>
                )}
            </Stack>

            <Flex w="100%" justify="space-between" mt="20px">
                <Button
                    variant="light"
                    fontSize="sm"
                    w="125px"
                    onClick={onClickBack}
                >
                    {t("system.actions.back")}
                </Button>
                <Button
                    w="125px"
                    variant="dark"
                    fontSize="sm"
                    onClick={handleOnSubmit}
                >
                    {t('system.actions.save')}
                </Button>
            </Flex>
        </>
    );
}

export default BoatImagesForm;